import * as React from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const DialogContext = React.createContext(null);

const Dialog = ({ open, onOpenChange, children }) => {
  if (!open) return null;
  
  return (
    <DialogContext.Provider value={{ onClose: () => onOpenChange?.(false) }}>
      {children}
    </DialogContext.Provider>
  );
};

const DialogTrigger = ({ children, ...props }) => children;

const DialogPortal = ({ children }) => children;

const DialogClose = ({ className, onClick, ...props }) => (
  <button
    className={cn(
      "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
      className
    )}
    onClick={onClick}
    {...props}
  >
    <X className="h-4 w-4" />
    <span className="sr-only">Close</span>
  </button>
);

const DialogOverlay = React.forwardRef(({ className, onClick, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80",
      className
    )}
    onClick={(e) => {
      // Only trigger close if clicking directly on the overlay
      if (e.target === e.currentTarget && onClick) {
        onClick(e);
      }
    }}
    {...props} />
))
DialogOverlay.displayName = "DialogOverlay"

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const context = React.useContext(DialogContext);
  const onClose = context?.onClose;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Content - rendered first in DOM, but visually on top */}
      <div
        ref={ref}
        className={cn(
          "relative z-20 grid w-full max-w-lg gap-4 border bg-white p-6 shadow-lg sm:rounded-lg m-4",
          className
        )}
        {...props}>
        {children}
        <button
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none"
          onClick={onClose}
          data-testid="dialog-close-btn"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </div>
      {/* Overlay - rendered after content in DOM but visually behind with lower z-index */}
      <div
        className="absolute inset-0 z-10 bg-black/80"
        onClick={onClose}
      />
    </div>
  );
})
DialogContent.displayName = "DialogContent"

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = "DialogTitle"

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = "DialogDescription"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
